import type { NetworkId } from "@alch/dx-entities";
import { pick } from "lodash";
import findLastIndex from "lodash/findLastIndex";
import { parse, stringify } from "qs";
import type { ComposerFormState } from "./form.js";
import { composerFormSchemaForMethod } from "./form.js";
import type { ComposerMethodConfig } from "./methods.js";
import { getUniqueMethodConfig } from "./methods.js";

const serializableProperties = ["network", "method", "query", "body"] as const;

export type SerializableComposerFormState = Pick<
  ComposerFormState,
  (typeof serializableProperties)[number]
>;

export const nullifyEmptyValues = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    // Trim empty values from the end of the array
    const arr = value.map(nullifyEmptyValues);
    return arr.slice(0, findLastIndex(arr, (val) => val != null) + 1);
  }

  if (typeof value === "object" && value != null) {
    const objEntries = Object.entries(value)
      .map(([key, value]) => [key, nullifyEmptyValues(value)])
      .filter((entry) => entry[1] != null);

    return objEntries.length === 0 ? undefined : Object.fromEntries(objEntries);
  }

  return value === "" || value == null ? undefined : value;
};

export const buildShareUrl = ({
  baseUrl,
  state,
}: {
  baseUrl: string;
  state: SerializableComposerFormState;
}): string => {
  const serializedState = stringify(pick(state, serializableProperties), {
    strictNullHandling: true,
    allowDots: true,
    allowEmptyArrays: true,
  });

  return `${baseUrl}/?${serializedState}`;
};

const booleanParser = (str: string, defaultDecoder: (str: string) => string) =>
  str === "true" ? true : str === "false" ? false : defaultDecoder(str);

export const parseStateFromShareUrl = ({
  methodConfigs,
  search,
}: {
  methodConfigs: ComposerMethodConfig[];
  search: string;
}): {
  state: SerializableComposerFormState;
  methodConfig: ComposerMethodConfig;
} | null => {
  const state = parse(search, {
    allowDots: true,
    ignoreQueryPrefix: true,
    depth: 10,
    strictNullHandling: true,
    allowEmptyArrays: true,
    decoder: booleanParser,
  });

  const methodConfig = getUniqueMethodConfig(methodConfigs, {
    method: String(state.method),
    network: state.network as NetworkId,
  });

  if (methodConfig == null) return null;

  const schema = composerFormSchemaForMethod(methodConfig, {
    strict: false,
  }).partial();

  const parseResult = schema.safeParse(state);

  if (!parseResult.success) return null;

  return {
    state: parseResult.data as SerializableComposerFormState,
    methodConfig,
  };
};
